import {
  type Channel,
  DOWNLOAD_ONLY_CONTENT_TYPE,
  type InboxMediaType,
  type InboxMessage,
  type InboxMessageAttachment,
  contentTypeEssence,
  isInlineMediaContentType,
} from '@sbc/domain-models/gateway';
import { Download, ExternalLink } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import { type InboxMediaCopy, useInboxAttachmentCopy, useInboxMediaCopy } from '@/lib/api/inbox';

export type MediaState =
  | { kind: 'ready'; url: string; download?: string; downloadOnly?: boolean }
  | { kind: 'loading' }
  | { kind: 'unavailable' }
  | { kind: 'not_shared' };

type MediaMessage = Pick<InboxMessage, 'mediaType' | 'mediaUrl' | 'mediaCopyStatus'>;

const NOUNS: Record<InboxMediaType, string> = {
  image: 'Photo',
  video: 'Video',
  audio: 'Voice message',
  file: 'File',
  share: 'Shared post',
  story: 'Story',
};

const LINKED_TYPES: ReadonlySet<InboxMediaType> = new Set(['share', 'story']);

export function wantsStoredCopy(message: MediaMessage): boolean {
  return message.mediaCopyStatus === 'stored' && !LINKED_TYPES.has(message.mediaType ?? 'file');
}

export function mediaStateOf(
  message: MediaMessage,
  copy: InboxMediaCopy,
  broken: boolean,
  channel?: Channel,
): MediaState {
  if (broken) {
    return { kind: 'unavailable' };
  }
  if (wantsStoredCopy(message)) {
    if (copy.file) {
      return {
        kind: 'ready',
        url: copy.file.url,
        download: downloadName(copy.file.type),
        downloadOnly: !isInlineMediaContentType(copy.file.type),
      };
    }
    if (!copy.failed) {
      return { kind: 'loading' };
    }
  }
  if (message.mediaCopyStatus === 'unavailable') {
    return { kind: 'unavailable' };
  }
  if (!message.mediaUrl && channel === 'whatsapp') {
    return message.mediaCopyStatus === null ? { kind: 'loading' } : { kind: 'unavailable' };
  }
  return message.mediaUrl ? { kind: 'ready', url: message.mediaUrl } : { kind: 'not_shared' };
}

export function mediaNoun(message: Pick<InboxMessage, 'mediaType' | 'mediaUrl'>): string {
  if (message.mediaType === 'share' && message.mediaUrl?.includes('/reel')) {
    return 'Shared reel';
  }
  if (message.mediaType === 'file' && !message.mediaUrl) {
    return 'Attachment';
  }
  return NOUNS[message.mediaType ?? 'file'];
}

function downloadName(contentType: string): string {
  const essence = contentTypeEssence(contentType);
  const subtype = essence.split('/')[1];
  return essence !== DOWNLOAD_ONLY_CONTENT_TYPE && subtype ? `attachment.${subtype}` : 'attachment';
}

export function MessageMedia({
  message,
  conversationId,
  channel,
}: {
  message: InboxMessage;
  conversationId: string;
  channel?: Channel;
}) {
  const copy = useInboxMediaCopy(conversationId, message.id, wantsStoredCopy(message));
  const [broken, setBroken] = useState(false);
  return (
    <MediaView
      type={message.mediaType ?? 'file'}
      noun={mediaNoun(message)}
      state={mediaStateOf(message, copy, broken, channel)}
      onBroken={() => setBroken(true)}
    />
  );
}

export function MediaView({
  type,
  noun,
  state,
  onBroken,
}: {
  type: InboxMediaType;
  noun: string;
  state: MediaState;
  onBroken?: () => void;
}) {
  if (state.kind === 'loading') {
    return <MediaNote>Loading {noun.toLowerCase()}…</MediaNote>;
  }
  if (state.kind === 'unavailable') {
    return <MediaNote>{noun} no longer available from Meta</MediaNote>;
  }
  if (state.kind === 'not_shared') {
    return <MediaNote>{noun} not shared by Meta. Open it in Business Suite.</MediaNote>;
  }
  if (state.downloadOnly || type === 'file') {
    return <FileLink url={state.url} download={state.download} />;
  }
  if (type === 'image') {
    return (
      <a href={state.url} target="_blank" rel="noreferrer" className="mb-1 block">
        <img
          src={state.url}
          alt={noun}
          className="max-h-64 rounded-md"
          loading="lazy"
          onError={onBroken}
        />
      </a>
    );
  }
  if (type === 'video') {
    return (
      <video
        controls
        preload="metadata"
        src={state.url}
        className="mb-1 max-h-80 max-w-full rounded-md"
        onError={onBroken}
      >
        <track kind="captions" />
      </video>
    );
  }
  if (type === 'audio') {
    return (
      <audio controls preload="metadata" src={state.url} className="mb-1" onError={onBroken}>
        <track kind="captions" />
      </audio>
    );
  }
  return (
    <a
      href={state.url}
      target="_blank"
      rel="noreferrer"
      className="mb-1 inline-flex items-center gap-1 text-xs font-medium underline"
    >
      <ExternalLink className="size-3.5" />
      {noun}
    </a>
  );
}

export type AttachmentState =
  | { kind: 'ready'; url: string; inline: boolean }
  | { kind: 'saving' }
  | { kind: 'not_saved'; reason: string };

export function attachmentStateOf(
  attachment: Pick<InboxMessageAttachment, 'copyStatus'>,
  copy: InboxMediaCopy,
  broken: boolean,
): AttachmentState {
  if (attachment.copyStatus === 'too_large') {
    return { kind: 'not_saved', reason: 'too large to keep a copy' };
  }
  if (attachment.copyStatus === null) {
    return { kind: 'saving' };
  }
  if (attachment.copyStatus !== 'stored' || copy.failed) {
    return { kind: 'not_saved', reason: 'could not be saved' };
  }
  if (!copy.file) {
    return { kind: 'saving' };
  }
  return {
    kind: 'ready',
    url: copy.file.url,
    inline: !broken && isInlineMediaContentType(copy.file.type),
  };
}

export function MessageAttachment({
  attachment,
  conversationId,
  messageId,
}: {
  attachment: InboxMessageAttachment;
  conversationId: string;
  messageId: string;
}) {
  const copy = useInboxAttachmentCopy(
    conversationId,
    messageId,
    attachment.id,
    attachment.copyStatus === 'stored',
  );
  const [broken, setBroken] = useState(false);
  const state = attachmentStateOf(attachment, copy, broken);
  if (state.kind === 'saving') {
    return <MediaNote>Saving {attachment.name}…</MediaNote>;
  }
  if (state.kind === 'not_saved') {
    return (
      <MediaNote>
        {attachment.name} {state.reason}
      </MediaNote>
    );
  }
  if (!state.inline || attachment.mediaType === 'file') {
    return (
      <a
        href={state.url}
        download={attachment.name}
        className="mb-1 flex items-center gap-1 text-xs font-medium underline"
      >
        <Download className="size-3.5 shrink-0" />
        <span className="truncate">{attachment.name}</span>
      </a>
    );
  }
  return (
    <MediaView
      type={attachment.mediaType}
      noun={attachment.name}
      state={{ kind: 'ready', url: state.url }}
      onBroken={() => setBroken(true)}
    />
  );
}

function FileLink({ url, download }: { url: string; download?: string }) {
  const className = 'mb-1 inline-flex items-center gap-1 text-xs font-medium underline';
  if (download) {
    return (
      <a href={url} download={download} className={className}>
        <Download className="size-3.5" />
        Download file
      </a>
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className={className}>
      <Download className="size-3.5" />
      Open file
    </a>
  );
}

function MediaNote({ children }: { children: ReactNode }) {
  return <p className="mb-1 text-xs font-medium italic opacity-80">{children}</p>;
}
